import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const MediaAndEntertainment = () => {
  const phrases = [
    "Broadcast & OTT Optimization",
    "Global Asset Supply Chains"
  ];

  const cardData = [
    { type: 'purple-clay', content: '🎬' },
    { type: 'smooth-wave', content: '📡' },
    { type: 'abstract-blocks', content: '🎞️' },
    { type: 'pink-sphere', content: '📊' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "🎬", title: "Post-Production Workflows", desc: "Manage localized proxy pipelines, complex editorial routing, and continuous master QC baseline runs across remote post-production hubs." },
    { icon: "📡", title: "MAM & VOD Delivery Auditing", desc: "Enforce multi-platform metadata compliance, automated profile transcode verifications, and structured version tracking matrices." },
    { icon: "🎞️", title: "Content Localization & Ingestion", desc: "Delegate dynamic foreign language dub schedules, structured subtitle batch timing verifications, and compliance editing tracks." },
    { icon: "🧠", title: "Predictive Audience Sensing", desc: "Access high-tier historical consumption velocity data, local viewing index trends, platform recommendation loops, and churn prevention triggers." },
    { icon: "📋", title: "Rights & Royalty Clearance", desc: "Outsource music sync cue sheet mapping, regional streaming territory permit logs, and syndication residual payout checklist audits securely." },
    { icon: "🔄", title: "Multi-Platform Asset Logistics", desc: "Integrate high-speed cloud egress telemetry streams with centralized orchestration hubs to maintain high distribution safety." },
    { icon: "🛡️", title: "Content Security & DRM Shielding", desc: "Enforce strict forensic watermarking protocols, active token validation sequences, and studio-grade anti-piracy operations." }
  ];

  const advancedServices = [
    { icon: "📊", title: "Live Stream Stream Quality Tracking", desc: "Transform real-time playback telemetry directly into automated CDN distribution adjustments using specialized edge-delivery operational models." },
    { icon: "👥", title: "Dedicated Content Moderation Pods", desc: "Deploy variable asset review groups trained explicitly in your custom platform safety guidelines and regional legal regulations." },
    { icon: "🌍", title: "Global Licensing Corridor Clearing", desc: "Deliver frictionless international syndication master transfers, handling transport formatting, regional customs, and complex audio configurations." },
    { icon: "🗂️", title: "Legacy Library Tape Migrations", desc: "Convert unstructured tape archives or linear broadcast files into indexed, AI-tagged cloud MAM object repositories." },
    { icon: "🎯", title: "Dynamic Ad Insertion Optimization", desc: "Accelerate server-side manifest manipulation using real-time inventory matching tools and delivery buffer protectors." },
    { icon: "🔧", title: "Fast Channel Packaging Frameworks", desc: "Combine direct cloud playout linear scheduling tools with automated EPG generation services to eliminate launch delays completely." },
    { icon: "⚖️", title: "SLA Broadcast Handover Shields", desc: "Enforce absolute transparency across distribution satellite and terrestrial milestones with continuous stream health tracking." },
    { icon: "🛣️", title: "High-Bandwidth Network Routing Loops", desc: "Coordinate specialized point-to-point accelerated transport logistics, log complex packet loss paths, and secure remote event downlinks." },
    { icon: "🧪", title: "Asset Pipeline Vulnerability Checks", desc: "Conduct regular master storage architectural safety audits to eliminate content creation friction points and protect total target output rates." }
  ];

  const technologies = {
    "MAM & Orchestration Systems": ["Avid MediaCentral / Signiant Engine", "Dalet Flex Enterprise Stack", "Sony Ci Cloud Orchestration", "EditShare Media Management Matrix"],
    "Transcoding & Delivery Hubs": ["AWS Elemental MediaLive / MediaConvert", "Harmonic VOS Playout Hubs", "Telestream Vantage Processing Blocks", "Bitmovin Encoding Core"],
    "CDN & Streaming Integration": ["Akamai EdgeWorkers Enterprise API", "Fastly Media Shield Trackers", "Cloudflare Stream Multi-CDN Nodes", "Limelight High-Volume Core"],
    "Metadata & Rights Engines": ["Gracenote Smart Data Systems", "Mediaocean Spot Management", "Rightsline Enterprise Platform", "Wideorbit Traffic Networks"],
    "Security & Protection Frameworks": ["Irdeto Cloaked CA Engine", "Intertrust ExpressPlay DRM Loop", "Verimatrix Watermarking Sync", "NAGRA Security Data Links"]
  };

  const processSteps = [
    { num: "01", title: "Media Workflow Mapping", desc: "Auditing active ingestion pipelines, isolating processing bottlenecks, and capturing current rendering throughput limits." },
    { num: "02", title: "SLA Performance Targets", desc: "Establishing strict bit-rate thresholds, drop-frame specifications, delivery timeline parameters, and transcoding profiles." },
    { num: "03", title: "MAM System Integration", desc: "Setting up secure API automation pipelines, multi-cloud storage bucket access privileges, and monitoring infrastructure links." },
    { num: "04", title: "Operations Personnel Alignment", desc: "Training specialized master control operators on unique distribution rules, caption sync guidelines, and emergency failover paths." },
    { num: "05", title: "Pilot Delivery Trials", desc: "Launching low-volume pilot distribution channels across select edge regions to isolate platform delivery friction spots early." },
    { num: "06", title: "Full Scale Distribution Run", desc: "Scaling aggregate streaming delivery output up to peak live event or premiere volume expectations with absolute load stability." },
    { num: "07", title: "Continuous Stream Calibration", desc: "Reviewing delivery error codes weekly, identifying CDN edge node choke points, and deploying regional routing modifications." }
  ];

  const industries = [
    "OTT Streaming Platforms", "Broadcast Television Networks", "Theatrical Production Studios", "Digital Content Creators",
    "Live Sports Event Broadcasters", "Fast Channel Aggregators", "Video Game Development Publishers", "Music Catalog Managers",
    "Post-Production Facilities", "Global Syndication Networks", "Corporate Enterprise Media Portfolios", "Interactive Entertainment Hubs"
  ];

  const designBenefits = [
    "Optimized Cloud Egress Expenses", "Elastic Production Scaling Models", "Strict Delivery Metadata Accuracy",
    "Continuous Quality-of-Experience Tracking", "Sharpened Focus on IP Acquisition", "Automated Compliance Ledger Creation",
    "Enhanced Subscriber Viewing Satisfaction", "Standardized Content Localization Paradigms"
  ];

  const whyChooseUs = [
    "Certified Broadcast Operations Engineers", "State-of-the-Art MAM Architectures", "Uncompromising Content Protection Standards",
    "Flawless Integration Strategy Execution", "Deep Multichannel Distribution Knowledge", "Flexible Operations Capacity Scaling",
    "Data-Driven CDN Traffic Routing", "Completely Transparent Performance Metrics", "Proven Enterprise SLA Maintenance",
    "Dedicated Content Quality Inspection Pods", "Modern Cloud Playout Architectures", "100% Broadcast Regulatory Compliance Guarantee"
  ];

  return (
    <>
      <Helmet>
        {/* Primary SEO */}
        <title>
          Media & Entertainment Solutions | Digital Media & Content Management | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Media & Entertainment Solutions | Digital Media & Content Management | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. delivers Media and Entertainment Solutions, including digital media management, content management systems (CMS), OTT platform solutions, media asset management, streaming solutions, advertising technology, audience analytics, and digital transformation services."
        />

        <meta
          name="keywords"
          content="Media and Entertainment Solutions, Media Software, Entertainment Solutions, Digital Media Solutions, Content Management System, CMS, OTT Platform Development, Video Streaming Solutions, Media Asset Management, Broadcasting Software, Advertising Technology, Audience Analytics, Digital Publishing, Media ERP, Entertainment Industry Software, Digital Transformation, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/media-entertainment-solutions"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Media & Entertainment Solutions | Digital Media Software | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Transform your media and entertainment business with Affsoft's solutions. We provide OTT platform development, content management, media asset management, streaming solutions, audience analytics, advertising technology, and digital transformation services."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/media-entertainment-solutions"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Media & Entertainment Solutions | Digital Media & Content Management | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Empower your media business with Affsoft IT Solutions. Our Media & Entertainment Solutions include OTT platforms, CMS, media asset management, video streaming, audience analytics, advertising technology, and digital transformation."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="process-outsourcing-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#0a0d14' // dark operational media room fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.15 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-primary fs-3">✳</div>
                <h1 className="fw-bold mb-3 display-5">Media And Entertainment</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At High Tech Solutions, we transform intricate media distribution workflows and global asset supply lines into high-efficiency operational assets. Our advanced outsourcing frameworks blend dedicated media operations specialists, modern orchestration software, and strict metric controls to reduce infrastructure costs and optimize content delivery pipelines.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>About High Tech Solutions</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor text-primary fs-4 animate-blink">|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SOLUTIONS SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-primary px-3 py-2 rounded-pill mb-2 text-uppercase">Operational Excellence</span>
              <h2 className="fw-bold text-dark display-6">Streamlined Operations Tailored for Measurable Efficiency</h2>
              <p className="text-muted mt-3">
                Managing non-core asset packaging and operational pipelines internally drains critical strategic bandwidth. At High Tech Solutions, we apply proven execution paradigms to secure content flows and accelerate daily business processes.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3 text-primary">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Operational Benefits Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Why Transition to Managed Operations Models?</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-primary fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED BUSINESS SYSTEMS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-dark text-white px-3 py-2 rounded-pill mb-2 text-uppercase">Intelligent Execution</span>
              <h2 className="fw-bold text-dark display-6">Performance Standards Backed by Rigorous Analytics</h2>
              <p className="text-muted mt-3">
                We leverage modern digital tools to add analytical precision to every outsourced task. From localized processing to compliance mastering, our services deliver stable delivery infrastructures built to protect your brand metrics.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3 text-dark">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-primary text-white px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold">Enterprise Tech Stack</span>
              <h2 className="fw-bold display-6">Software Infrastructure & Platforms We Deploy</h2>
              <p className="text-light-50">We run complex processing tasks using industry-standard secure operational suites.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold text-primary mb-3 border-bottom border-secondary pb-2">{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="text-primary opacity-75">•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Methodical Transition</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered Outsourcing Lifecycle</h2>
              <p className="text-muted">A transparent operational roadmap that securely moves processes from initial scope to stabilized metrics.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-primary border-4 h-100 shadow-sm">
                    <div className="fw-bold text-primary display-6 mb-2">{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Industries We Empower</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🎬 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY PARTNER WITH US ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with High Tech Solutions?</h2>
              <p className="text-muted">We combine strict data governance metrics with elite domain expertise to support your operations.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="text-primary fs-4">🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>
    </>
  );
};

export default MediaAndEntertainment;